﻿import { transformGeometryWithOptions } from 'ol/format/Feature.js';
import LineString from 'ol/geom/LineString.js';
import MultiLineString from 'ol/geom/MultiLineString.js';
import MultiPoint from 'ol/geom/MultiPoint.js';
import MultiPolygon from 'ol/geom/MultiPolygon.js';
import Point from 'ol/geom/Point.js';
import Polygon from 'ol/geom/Polygon.js';
import RenderFeature from 'ol/render/Feature.js';
import { inflateEnds } from 'ol/geom/flat/orient.js';
import { MVT } from 'ol/format';

class XMVT extends MVT {
    constructor(options) {
        super(options);
        this.layerId = options.layerId;
        this.selectionHandler = options.selectionHandler;
        this.generalActionHandler = options.generalActionHandler;
    }

    createFeature_(pbf, rawFeature, options) {
        const type = rawFeature.type;
        if (type === 0) {
            return null;
        }

        let feature;
        const values = rawFeature.properties;

        let id;
        if (!this.idProperty_) {
            id = rawFeature.id;
        } else {
            id = values[this.idProperty_];
            delete values[this.idProperty_];
        }

        values[this.layerName_] = rawFeature.layer.name;

        const flatCoordinates = /** @type {Array<number>} */ ([]);
        const ends = /** @type {Array<number>} */ ([]);
        this.readRawGeometry_(pbf, rawFeature, flatCoordinates, ends);

        const geometryType = getGeometryType(type, ends.length);

        if (this.featureClass_ === RenderFeature) {
            feature = new this.featureClass_(
                geometryType,
                flatCoordinates,
                ends,
                values,
                id
            );
            feature.transform(options.dataProjection);
        } else {
            let geom;
            if (geometryType == 'Polygon') {
                const endss = inflateEnds(flatCoordinates, ends);
                geom =
                    endss.length > 1
                        ? new MultiPolygon(flatCoordinates, 'XY', endss)
                        : new Polygon(flatCoordinates, 'XY', ends);
            } else {
                geom =
                    geometryType === 'Point'
                        ? new Point(flatCoordinates, 'XY')
                        : geometryType === 'LineString'
                            ? new LineString(flatCoordinates, 'XY')
                            : geometryType === 'MultiPoint'
                                ? new MultiPoint(flatCoordinates, 'XY')
                                : geometryType === 'MultiLineString'
                                    ? new MultiLineString(flatCoordinates, 'XY', ends)
                                    : null;
            }
            const ctor = /** @type {typeof import("../Feature.js").default} */ (
                this.featureClass_
            );
            feature = new ctor();
            if (this.geometryName_) {
                feature.setGeometryName(this.geometryName_);
            }
            const geometry = transformGeometryWithOptions(geom, false, options);
            feature.setGeometry(geometry);
            if (id !== undefined) {
                feature.setId(id);
            }
            feature.setProperties(values, true);
        }

        if (this.layerId != null) {
            feature.layerId = this.layerId;
        }

        return feature;
    }
}

/**
 * @param {number} type The raw feature's geometry type
 * @param {number} numEnds Number of ends of the flat coordinates of the
 * geometry.
 * @return {import("../geom/Geometry.js").Type} The geometry type.
 */
function getGeometryType(type, numEnds) {
    /** @type {import("../geom/Geometry.js").Type} */
    let geometryType;
    if (type === 1) {
        geometryType = numEnds === 1 ? 'Point' : 'MultiPoint';
    } else if (type === 2) {
        geometryType = numEnds === 1 ? 'LineString' : 'MultiLineString';
    } else if (type === 3) {
        geometryType = 'Polygon';
        // MultiPolygon not relevant for rendering - winding order determines
        // outer rings of polygons.
    }
    return geometryType;
}

export default XMVT;